import { createReference, getAllQuestionnaireAnswers, MedplumClient } from "@medplum/core";
import {
  Observation,
  Questionnaire,
  QuestionnaireItem,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
} from "@medplum/fhirtypes";

import { AcceptedIntakeAbortionCounts } from "@/clients/interfaces/gtpal";
import { findCurrentIntakeQuestionnaireResponse } from "@/clients/services/current-intake";
import { getAcceptedIntakeAbortionCounts } from "@/clients/utils/gtpal-abortions";
import { LOINCCodes } from "@/fhir/enums/loinc";
import { Questionnaires } from "@/fhir/enums/questionnaires";
import { Systems } from "@/fhir/enums/systems";
import { createFormInitialValues } from "@/intake-forms/utils/intake-forms";
import { setAccounts } from "@/utils/accounts";
import { getResourcesFromBundle, joinReferenceString } from "@/utils/medplum";
import { showSystemErrorNotification } from "@/utils/notifications";
import {
  getQuestionnaireResponseFromFormValues,
  QuestionnaireFormValues,
} from "@/utils/questionnaires";

interface GTPALAnswers {
  gtpalGravida: Observation | undefined;
  gtpalTerm: Observation | undefined;
  gtpalPreterm: Observation | undefined;
  gtpalAbortionsInduced: Observation | undefined;
  gtpalMiscarriages: Observation | undefined;
  gtpalLiving: Observation | undefined;
}

interface GTPALAllAnswers {
  gtpal: GTPALAnswers;
  pregnancyDetails: Observation[];
}

export async function fetchGTPALQuestionnaire(medplum: MedplumClient): Promise<Questionnaire> {
  const response = await medplum.searchResources("Questionnaire", {
    url: Questionnaires.GTPAL,
    _count: 1,
    _sort: "-_lastUpdated",
  });

  if (response.bundle.entry?.length === 0) {
    showSystemErrorNotification();
    throw new Error("GTPAL Questionnaire not found");
  }

  return getResourcesFromBundle("Questionnaire", response.bundle)[0];
}

export async function fetchGTPALQuestionnaireResponse(
  medplum: MedplumClient,
  questionnaire: Questionnaire,
  clientId: string,
): Promise<QuestionnaireResponse | undefined> {
  const response = await medplum.searchResources("QuestionnaireResponse", {
    subject: joinReferenceString("Patient", clientId),
    questionnaire: createReference(questionnaire).reference,
    _count: 1,
    _sort: "-_lastUpdated",
  });

  return getResourcesFromBundle("QuestionnaireResponse", response.bundle)[0];
}

export async function createGTPALQuestionnaireResponse(
  medplum: MedplumClient,
  questionnaire: Questionnaire,
  clientId: string,
  values: QuestionnaireFormValues,
  previousQuestionnaireResponse: QuestionnaireResponse | undefined,
): Promise<QuestionnaireResponse> {
  const profile = medplum.getProfile();

  if (!profile) {
    showSystemErrorNotification();
    throw new Error("Profile not found");
  }

  const subject = joinReferenceString("Patient", clientId);
  const questionnaireResponse = getQuestionnaireResponseFromFormValues(
    questionnaire,
    values,
    {
      reference: subject,
    },
    {
      reference: joinReferenceString("Practitioner", profile.id!),
    },
    null,
    previousQuestionnaireResponse?.identifier,
  );

  const newQuestionnaireResponse = await medplum.createResource(questionnaireResponse);
  await setAccounts(medplum, newQuestionnaireResponse, [], { extractFromPatient: true });
  return newQuestionnaireResponse;
}

/**
 * Builds the GTPAL drawer's initial form values.
 *
 * An abortion component with no GTPAL value is prefilled from the client's accepted intake
 * answer, the same value the GTPAL bot counts for a blank component.
 *
 * @param medplum Authenticated Medplum client.
 * @param subject `Patient/{id}` of the client.
 * @param questionnaireItems The GTPAL questionnaire group's items.
 * @param questionnaireResponse The client's latest GTPAL questionnaire response, if any.
 * @returns The form's initial values.
 */
export async function createGTPALInitialValues(
  medplum: MedplumClient,
  subject: string,
  questionnaireItems: QuestionnaireItem[],
  questionnaireResponse: QuestionnaireResponse | undefined,
) {
  const [observations, intakeQuestionnaireResponse] = await Promise.all([
    fetchAllGTPALObservations(medplum, subject),
    findCurrentIntakeQuestionnaireResponse(medplum, subject),
  ]);
  const generatedQuestionnaireResponse = generateGTPALQuestionnaireResponseFromObservations(
    questionnaireResponse,
    observations,
    getAcceptedIntakeAbortionCounts(intakeQuestionnaireResponse?.item),
  );
  return createFormInitialValues(questionnaireItems ?? [], generatedQuestionnaireResponse?.item);
}

async function fetchAllGTPALObservations(
  medplum: MedplumClient,
  subject: string,
): Promise<GTPALAllAnswers> {
  // Each observation must be fetched separately since we can't ensure that the _lastUpdated sort
  // will return the GTPAL observations in the correct order without duplicates.
  const [gtpalGravida, gtpalTerm, gtpalPreterm, abortionsInduced, miscarriages, gtpalLiving] =
    await Promise.all([
      fetchGTPALObservation(medplum, subject, LOINCCodes.NPregnancies),
      fetchGTPALObservation(medplum, subject, LOINCCodes.NFullTerm),
      fetchGTPALObservation(medplum, subject, LOINCCodes.NPremature),
      fetchGTPALObservation(medplum, subject, LOINCCodes.NAbortionsInduced),
      fetchGTPALObservation(medplum, subject, LOINCCodes.NMiscarriages),
      fetchGTPALObservation(medplum, subject, LOINCCodes.NLivingChildren),
    ]);

  const gtpalObservations = {
    gtpalGravida,
    gtpalTerm,
    gtpalPreterm,
    gtpalAbortionsInduced: abortionsInduced,
    gtpalMiscarriages: miscarriages,
    gtpalLiving,
  };

  const pregnancyDetailsObservations = await medplum.searchResources("Observation", {
    subject,
    code: `${Systems.LOINC}|${LOINCCodes.OutcomeOfPregnancy}`,
    _sort: "-_lastUpdated",
  });

  return {
    gtpal: gtpalObservations,
    pregnancyDetails: pregnancyDetailsObservations,
  };
}

async function fetchGTPALObservation(medplum: MedplumClient, subject: string, code: string) {
  return await medplum.searchOne("Observation", {
    subject,
    code: `${Systems.LOINC}|${code}`,
    _sort: "-_lastUpdated",
  });
}

interface GTPALLinkIdConfig {
  linkId: string;
  text: string;
  intakeFallback: number | undefined;
}

function generateGTPALQuestionnaireResponseFromObservations(
  questionnaireResponse: QuestionnaireResponse | undefined,
  observations: GTPALAllAnswers,
  intakeAbortionCounts: AcceptedIntakeAbortionCounts,
) {
  const answers = questionnaireResponse ? getAllQuestionnaireAnswers(questionnaireResponse) : {};

  const gtpalLinkIds: GTPALLinkIdConfig[] = [
    { linkId: "gtpalGravida", text: "Gravida (G)", intakeFallback: undefined },
    { linkId: "gtpalTerm", text: "Term (T)", intakeFallback: undefined },
    { linkId: "gtpalPreterm", text: "Preterm (P)", intakeFallback: undefined },
    {
      linkId: "gtpalAbortionsInduced",
      text: "Abortions (induced)",
      intakeFallback: intakeAbortionCounts.abortionsInduced,
    },
    {
      linkId: "gtpalMiscarriages",
      text: "Miscarriages",
      intakeFallback: intakeAbortionCounts.miscarriages,
    },
    { linkId: "gtpalLiving", text: "Living (L)", intakeFallback: undefined },
  ];
  const questionnaireResponseItemGroup = {
    ...questionnaireResponse?.item?.[0],
    item: [
      {
        linkId: "gtpal",
        text: "GTPAL",
        item: [
          ...gtpalLinkIds.map(({ linkId, text, intakeFallback }) => ({
            linkId,
            text,
            answer: [
              {
                valueInteger:
                  getGTPALAnswer(
                    questionnaireResponse,
                    answers,
                    linkId,
                    observations.gtpal[linkId as keyof GTPALAnswers],
                  ) ?? intakeFallback,
              },
            ],
          })),
          {
            linkId: "gtpalCanUpdateIntake",
            text: "Can update intake?",
            type: "boolean",
            answer: [
              {
                valueBoolean: true,
              },
            ],
          },
        ],
      },
    ],
  } as QuestionnaireResponseItem;

  // TODO: do the same answer parsing for pregnancyDetails
  const pregnancyDetails = questionnaireResponse?.item?.[0].item?.filter(
    (item) => item.linkId === "pregnancyDetailsFormset",
  );
  if (pregnancyDetails?.length) {
    questionnaireResponseItemGroup.item?.push(...pregnancyDetails);
  }

  return {
    ...(questionnaireResponse ?? {}),
    item: [questionnaireResponseItemGroup],
  };
}

function getGTPALAnswer(
  questionnaireResponse: QuestionnaireResponse | undefined,
  answers: Record<string, QuestionnaireResponseItemAnswer[]>,
  linkId: string,
  observation: Observation | undefined,
) {
  const questionnaireResponseLastUpdated = questionnaireResponse?.meta?.lastUpdated;
  if (
    observation?.meta?.lastUpdated &&
    (!questionnaireResponseLastUpdated ||
      new Date(questionnaireResponseLastUpdated) < new Date(observation.meta.lastUpdated))
  ) {
    return observation.valueInteger;
  }

  return answers[linkId]?.[0]?.valueInteger;
}
